import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { HTMLProps, useEffect, useRef } from "react";
import LinkWithParams from "components/LinkWithParams";
import HighlightText from "components/HighlightText";
import { ListFilterParams } from "components/typings";
import { taskBasePriceDisplay } from "pages/project/common/taskBase/taskBasePrice";
import { TaskBase } from "pages/project/common/taskBase.typing";

export function useOfferListTable({
  filterParams,
  canSelect,
}: {
  filterParams: ListFilterParams<TaskBase>;
  canSelect?: boolean;
}) {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<TaskBase>();

  const columns = [
    ...(canSelect
      ? [
          columnHelper.display({
            id: "select",
            size: 40,
            header: ({ table }) => (
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllRowsSelected(),
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler(),
                }}
              />
            ),
            cell: (info) => (
              <IndeterminateCheckbox
                {...{
                  checked: info.row.getIsSelected(),
                  onChange: info.row.getToggleSelectedHandler(),
                }}
              />
            ),
          }),
        ]
      : []),
    columnHelper.accessor("id", {
      header: t("task-base.fields.id"),
      cell: (info) => (
        <div className="flex items-center space-x-2">
          <LinkWithParams
            to={`./${info.getValue()}`}
            className="flex flex-row items-center whitespace-nowrap text-base font-medium underline hover:text-blue-500"
          >
            <HighlightText text={`${info.row.original.taskCode}`} term={filterParams.term} />
          </LinkWithParams>
          {info.row.original.shouldShowFeedbackIcon && (
            <ClipboardDocumentListIcon className="size-4 text-blue-500" title={t("feedback.pendingFeedback")} />
          )}
        </div>
      ),
    }),
    columnHelper.accessor("projectName", {
      size: 300,
      header: t("task-base.fields.projectName"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-projectName-value`}
        />
      ),
    }),
    columnHelper.accessor("customerName", {
      size: 200,
      header: t("task-base.fields.customer"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-customerName-value`}
        />
      ),
    }),
    columnHelper.accessor("supplierContactName", {
      header: t("task-base.fields.supplierContact"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-supplierContact`}
        />
      ),
    }),
    columnHelper.accessor("taskType", {
      header: t("task-base.fields.type"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-taskType-value`}
        />
      ),
    }),
    columnHelper.accessor("sourceLanguage", {
      header: t("task-base.fields.source"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-sourceLanguage-value`}
        />
      ),
    }),
    columnHelper.accessor("targetLanguage", {
      header: t("task-base.fields.target"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-targetLanguage-value`}
        />
      ),
    }),
    columnHelper.accessor("startDate", {
      header: t("task-base.fields.estimatedStartOn"),
      cell: (info) => (
        <HighlightText
          text={
            info.getValue()
              ? t("enum:dates.mediumDateTime", { date: info.getValue() })
              : t("task-base.fields.startOnEmpty")
          }
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-startDate-value`}
        />
      ),
    }),
    columnHelper.accessor("dueDate", {
      header: t("task-base.fields.dueOn"),
      cell: (info) => (
        <HighlightText
          text={t("enum:dates.mediumDateTime", { date: info.getValue() })}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-dueDate-value`}
        />
      ),
    }),
    columnHelper.accessor("quantity", {
      header: t("task-base.fields.quantity"),
      cell: (info) => (
        <HighlightText
          text={`${t("enum:intlNumber", { val: info.row.original.quantity })} ${info.row.original.unit}`}
          term={filterParams.term}
          className="whitespace-nowrap"
          dataTestId={`${getDataTestPrefix(info)}-quantity-value`}
        />
      ),
    }),
    columnHelper.accessor("price", {
      header: t("task-base.fields.total"),
      cell: (info) => (
        <HighlightText
          text={taskBasePriceDisplay(info.row.original)}
          term={filterParams.term}
          className="whitespace-nowrap"
          dataTestId={`${getDataTestPrefix(info)}-total-value`}
        />
      ),
    }),
  ];

  return { columns };
}

function IndeterminateCheckbox({ indeterminate, ...rest }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      {...rest}
      onClick={(event) => {
        event.stopPropagation();
        rest.onClick?.(event);
      }}
      className="size-4 rounded border-gray-300 focus:ring-ribbon-300 dark:focus:ring-offset-gray-700"
    />
  );
}

function getDataTestPrefix(cellContext: CellContext<TaskBase, unknown>) {
  return `row-offer-${cellContext.row.original.id}-`;
}
